import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { GET_FABRICS } from "../../apollo/fabric/query";
import { SHOP_SIZES } from "../../apollo/setting_apollo/query";
import { CREATE_MANY_ORDER_WITH_ORDER_GROUP_CUSTOM } from "../../apollo/order/mutation";
import { GET_USER_CONFIRM } from "../../apollo/userconfirm/query";
import { LaoAddress } from "../../const/laoAddress";
import Swal from "sweetalert2";
import CreateCustomerModal from "./CreateCustomerModal";
import { FaCheckCircle } from "react-icons/fa";

const CreateOrderModal = ({ show, onHide, shopId, products }) => {
  const [formData, setFormData] = useState({
    code: "",
    firstName: "",
    lastName: "",
    phone: "",
    village: "",
    district: "",
    province: "",
    receiveDate: "",
    payment: "CASH",
    receivedMoney: "",
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);

  const [getUserConfirm, { data: getuserConfirmData }] = useLazyQuery(GET_USER_CONFIRM, { fetchPolicy: "cache-and-network" });
  
  useEffect(() => {
    if (showCustomerDropdown && shopId) {
      getUserConfirm({
        variables: {
          where: {
            shop: shopId,
            isDeleted: false,
            ...(customerSearchTerm ? { searchKeyWord: customerSearchTerm } : {})
          },
          limit: 10,
          orderBy: "createdAt_DESC"
        }
      });
    }
  }, [customerSearchTerm, showCustomerDropdown, shopId, getUserConfirm]);

  const customerList = getuserConfirmData?.userconfirms?.data || [];

  const [selectedProducts, setSelectedProducts] = useState([]);

  const [getFabrics, { data: fabricData }] = useLazyQuery(GET_FABRICS, {
    fetchPolicy: "network-only",
  });
  const [getShopSizes, { data: shopSizeData }] = useLazyQuery(SHOP_SIZES, {
    fetchPolicy: "network-only",
  });

  const [createOrdergroupCustomize, { loading: creatingOrder }] = useMutation(
    CREATE_MANY_ORDER_WITH_ORDER_GROUP_CUSTOM
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (show && shopId) {
      getFabrics({ variables: { where: { shop: shopId, isDeleted: false } } });
      getShopSizes({ variables: { shop: shopId } });
    }
  }, [show, shopId, getFabrics, getShopSizes]);

  useEffect(() => {
    if (!show) {
      setFormData({
        code: "",
        firstName: "",
        lastName: "",
        phone: "",
        village: "",
        district: "",
        province: "",
        receiveDate: "",
        payment: "CASH",
        receivedMoney: "",
      });
      setSelectedProducts([]);
      setSearchTerm("");
      setShowDropdown(false);
      setSelectedCustomer(null);
      setCustomerSearchTerm("");
      setShowCustomerDropdown(false);
    }
  }, [show]);

  const fabrics = fabricData?.fabrics?.data || [];
  const shopSizes = shopSizeData?.shopSizes || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleProductSelect = (product) => {
    // Check if the product has isPrinted directly or inside a nested product object (for variants)
    const isPrintedStatus = product.isPrinted === true || product.product?.isPrinted === true;
    console.log("Selected Product:", product);
    console.log("isPrintedStatus:", isPrintedStatus);

    setSelectedProducts([
      ...selectedProducts,
      {
        product: { ...product, isPrinted: isPrintedStatus },
        fabricId: "",
        sizeQuantities: {},
        quantity: 1,
        note: "",
      },
    ]);
    setShowDropdown(false);
    setSearchTerm(""); // Clear search term to allow searching another product easily
  };

  const handleRemoveProduct = (index) => {
    const newProducts = [...selectedProducts];
    newProducts.splice(index, 1);
    setSelectedProducts(newProducts);
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...selectedProducts];
    newProducts[index][field] = value;
    setSelectedProducts(newProducts);
  };

  const handleProductSizeChange = (index, sizeId, value) => {
    const newProducts = [...selectedProducts];
    newProducts[index].sizeQuantities = {
      ...newProducts[index].sizeQuantities,
      [sizeId]: value === "" ? "" : parseInt(value) || 0,
    };
    setSelectedProducts(newProducts);
  };

  const toggleProductSize = (index, sizeId) => {
    const newProducts = [...selectedProducts];
    const sizeQuantities = { ...newProducts[index].sizeQuantities };
    
    if (sizeQuantities.hasOwnProperty(sizeId)) {
      delete sizeQuantities[sizeId];
    } else {
      sizeQuantities[sizeId] = "";
    }
    
    newProducts[index].sizeQuantities = sizeQuantities;
    setSelectedProducts(newProducts);
  };

  const filteredProducts = products?.filter((p) => {
    const isSelected = selectedProducts.some((sp) => sp.product.id === p.id);
    return !isSelected && p.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const calculateProductPrice = (sp) => {
    if (sp.product.isPrinted) {
      const selectedFabric = fabrics.find((f) => f.id === sp.fabricId);
      const fabricPrice = selectedFabric ? selectedFabric.price : 0;
      let totalQuantity = 0;
      if (sp.sizeQuantities) {
        Object.values(sp.sizeQuantities).forEach((q) => {
          totalQuantity += parseInt(q) || 0;
        });
      }
      return totalQuantity * fabricPrice;
    } else {
      return (parseInt(sp.quantity) || 0) * (sp.product.price || 0);
    }
  };

  const calculateTotalOrderPrice = () => {
    return selectedProducts.reduce((total, sp) => total + calculateProductPrice(sp), 0);
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      Swal.fire("ແຈ້ງເຕືອນ", "ກະລຸນາເລືອກສິນຄ້າ", "warning");
      return;
    }

    try {
      const orders = selectedProducts.map((p) => {
        let note = p.note || "";
        let shopSize = "";
        let fabric = "";
        
        let amount = p.product.isPrinted
          ? Object.values(p.sizeQuantities).reduce(
              (acc, q) => acc + (parseInt(q) || 0),
              0
            )
          : parseInt(p.quantity) || 1;
        let price = p.product.isPrinted
          ? fabrics.find((f) => f.id === p.fabricId)?.price || 0
          : p.product.price || 0;

        if (p.product.isPrinted) {
          const sizeNotes = Object.entries(p.sizeQuantities)
            .filter(([szId, q]) => parseInt(q) > 0)
            .map(
              ([szId, q]) =>
                `${shopSizes.find((s) => s.id === szId)?.value || szId}(${q})`
            )
            .join(", ");
          
          shopSize = sizeNotes;
            
          const fabricName =
            fabrics.find((f) => f.id === p.fabricId)?.name || "";
            
          fabric = fabricName;
        }

        const totalPrice = calculateProductPrice(p);

        return {
          product: p.product.id,
          stock: p.product.id, // V4 usually expects stock to be populated
          productVariant: p.product.productVariant ?? null, // Match Carts.js
          amount: amount,
          price: price,
          totalPrice: totalPrice,
          totalPriceBaht: 0,
          totalPriceUsd: 0,
          note: note,
          shopSize: shopSize || null,
          fabric: fabric || null,
          fabricId: p.fabricId || null,
          currency: p.product.currency || "LAK",
          commissionPlatform: "POS", // Default fallback
          commissionType: null,
          commissionValue: 0,
        };
      });

      const totalOrderPrice = calculateTotalOrderPrice();
      const totalAmount = orders.reduce((sum, order) => sum + order.amount, 0);
      const received = parseFloat(formData.receivedMoney) || 0;
      const change = received - totalOrderPrice > 0 ? received - totalOrderPrice : 0;

      const orderGroup = {
        shop: shopId,
        customerName: formData.firstName,
        customerLastName: formData.lastName,
        phone: formData.phone,
        village: formData.village,
        district: formData.district,
        province: formData.province,
        paymentDate: formData.receiveDate || new Date().toISOString(),
        currency: "LAK",
        amount: totalAmount, // Total quantities across orders
        totalPrice: totalOrderPrice,
        sumPrice: totalOrderPrice,
        sumPriceBaht: 0,
        sumPriceUsd: 0,
        payment: formData.payment, 
        change: change,
        receivedMoney: received,
        isPaymented: true,
        commissionPlatforms: "POS", // Default fallback
        ...(formData.code ? { code: formData.code } : {}),
        ...(selectedCustomer ? { userConfirm: selectedCustomer.id } : {}),
      };

      const payload = {
        orders,
        orderGroup,
      };

      console.log("Submit Order Payload (V4-like structure): ", payload);

      const res = await createOrdergroupCustomize({
        variables: { data: payload },
      });

      if (res.data?.createOrdergroupCustomize?.id) {
        Swal.fire("ສຳເລັດ", "ບັນທຶກອໍເດີສຳເລັດແລ້ວ", "success");
        onHide();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      Swal.fire("ຜິດພາດ", "ບໍ່ສາມາດບັນທຶກອໍເດີໄດ້: " + error.message, "error");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18, fontWeight: "bold" }}>
          ສ້າງອໍເດີ
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>ລະຫັດອໍເດີ (Code) - <span className="text-muted" style={{fontSize: '12px'}}>ປ່ອຍຫວ່າງໄວ້ເພື່ອໃຫ້ລະບົບສ້າງອັດຕະໂນມັດ</span></Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນລະຫັດອໍເດີ (ຖ້າມີ)"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group style={{ position: "relative" }}>
                <Form.Label>ຄົ້ນຫາລູກຄ້າ / ສະມາຊິກ (ເລືອກ ຫຼື ພິມຊື່ໃໝ່ໄດ້ເລີຍ)</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="ຄົ້ນຫາຊື່ ຫຼື ເບີໂທລູກຄ້າ..."
                    value={customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                      setShowCustomerDropdown(true);
                      // ຖ້າພິມຊື່ໃໝ່ ກໍໃຫ້ອັບເດດ firstName ໄປພ້ອມເລີຍ ແລະ clear selectedCustomer
                      setFormData({ ...formData, firstName: e.target.value });
                      if (selectedCustomer) setSelectedCustomer(null);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                  <Button
                    variant="outline-success"
                    className="ms-2"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => {
                      setShowCreateCustomerModal(true);
                    }}
                  >
                    + ເພີ່ມລູກຄ້າ
                  </Button>
                </div>
                {showCustomerDropdown && customerList?.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      zIndex: 1000,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {customerList.map((c) => (
                      <div
                        key={c.id}
                        style={{
                          padding: "10px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onClick={() => {
                          setSelectedCustomer(c);
                          setCustomerSearchTerm(`${c.name || ""} ${c.lastname || ""}`.trim());
                          setFormData({
                            ...formData,
                            firstName: c.name || "",
                            lastName: c.lastname || "",
                            phone: c.phone || "",
                            province: c.address?.province || "",
                            district: c.address?.district || "",
                            village: c.address?.village || "",
                          });
                          setShowCustomerDropdown(false);
                        }}
                      >
                        {c.name} {c.lastname} {c.phone ? `(${c.phone})` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>ຊື່</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນຊື່"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>ນາມສະກຸນ</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນນາມສະກຸນ"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>ເບີໂທ</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນເບີໂທ"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>ວັນທີຮັບເຄື່ອງ</Form.Label>
                <Form.Control
                  type="date"
                  name="receiveDate"
                  value={formData.receiveDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>ແຂວງ</Form.Label>
                <Form.Control
                  as="select"
                  name="province"
                  value={formData.province}
                  onChange={(e) => {
                    setFormData({ ...formData, province: e.target.value, district: "" });
                  }}
                >
                  <option value="">--ເລືອກ--</option>
                  {LaoAddress.map((p, index) => (
                    <option key={"province" + index} value={p?.province_name}>
                      {p?.province_name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ເມືອງ</Form.Label>
                <Form.Control
                  as="select"
                  name="district"
                  disabled={!formData.province}
                  value={formData.district}
                  onChange={handleInputChange}
                >
                  <option value="">--ເລືອກ--</option>
                  {formData.province &&
                    LaoAddress.find((f) => f.province_name === formData.province)?.district_list?.map((p, index) => (
                      <option key={"district" + index} value={p?.district}>
                        {p?.district}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ບ້ານ</Form.Label>
                <Form.Control
                  type="text"
                  name="village"
                  value={formData.village}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນບ້ານ"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>ປະເພດການຊຳລະ</Form.Label>
                <Form.Control
                  as="select"
                  name="payment"
                  value={formData.payment}
                  onChange={handleInputChange}
                >
                  <option value="CASH">ເງິນສົດ</option>
                  <option value="TRANSFER">ເງິນໂອນ</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ຮັບເງິນມາມູນຄ່າ</Form.Label>
                <Form.Control
                  type="number"
                  name="receivedMoney"
                  value={formData.receivedMoney}
                  onChange={handleInputChange}
                  placeholder="ປ້ອນຈຳນວນເງິນ"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>ເງິນທອນ</Form.Label>
                <Form.Control
                  type="number"
                  readOnly
                  value={Math.max(0, (parseFloat(formData.receivedMoney) || 0) - calculateTotalOrderPrice())}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group style={{ position: "relative" }}>
                <Form.Label>ເລືອກສິນຄ້າ (ສາມາດເລືອກໄດ້ຫຼາຍລາຍການ)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ຄົ້ນຫາຊື່ສິນຄ້າເພື່ອເພີ່ມລົງໃນລາຍການ..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                />
                {showDropdown && filteredProducts?.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      zIndex: 1000,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {filteredProducts.map((p) => (
                      <div
                        key={p.id}
                        style={{
                          padding: "10px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onClick={() => handleProductSelect(p)}
                      >
                        {p.name} {p.isPrinted === true || p.product?.isPrinted === true ? <span style={{ color: "green", fontSize: "12px", marginLeft: "8px" }}>(ງານພິມ)</span> : ""}
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* ລາຍການສິນຄ້າທີ່ເລືອກແລ້ວ */}
          {selectedProducts.length > 0 && (
            <div className="mt-4">
              <h6 style={{ fontWeight: "bold", marginBottom: 15 }}>
                ລາຍການສິນຄ້າທີ່ເລືອກແລ້ວ ({selectedProducts.length})
              </h6>
              {selectedProducts.map((sp, index) => (
                <div
                  key={index}
                  style={{
                    background: "#f9fafb",
                    padding: 15,
                    borderRadius: 8,
                    marginBottom: 15,
                    position: "relative",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{ position: "absolute", top: 15, right: 15 }}
                    onClick={() => handleRemoveProduct(index)}
                  >
                    ລຶບ
                  </Button>
                  <h6
                    style={{
                      fontWeight: "bold",
                      marginBottom: 15,
                      paddingRight: "50px",
                      color: "#111827",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{index + 1}. {sp.product.name}</span>
                    <span style={{ color: "#2563eb" }}>
                      ລາຄາ: {calculateProductPrice(sp).toLocaleString()}
                    </span>
                  </h6>

                  {sp.product.isPrinted ? (
                    <>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label>ເລືອກຜ້າ</Form.Label>
                            <Form.Select
                              value={sp.fabricId}
                              onChange={(e) =>
                                handleProductChange(
                                  index,
                                  "fabricId",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">-- ເລືອກຜ້າ --</option>
                              {fabrics.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.name} - ລາຄາ: {f.price}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Label style={{ fontWeight: "bold", fontSize: "16px" }}>ໄຊຕ໌</Form.Label>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "15px" }}>
                            {shopSizes.map((sz) => {
                              const isSelected = sp.sizeQuantities.hasOwnProperty(sz.id);
                              return (
                                <div
                                  key={sz.id}
                                  onClick={() => toggleProductSize(index, sz.id)}
                                  style={{
                                    padding: "6px 16px",
                                    border: isSelected ? "1px solid #10b981" : "1px solid #d1d5db",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    backgroundColor: "white",
                                    color: isSelected ? "#374151" : "#6b7280",
                                    fontWeight: "normal",
                                    userSelect: "none"
                                  }}
                                >
                                  {isSelected && <FaCheckCircle style={{ fontSize: "14px", color: "#6b7280" }} />}
                                  {sz.value}
                                </div>
                              );
                            })}
                          </div>

                          {Object.keys(sp.sizeQuantities).length > 0 && (
                            <div style={{ backgroundColor: "#fff", padding: "15px", borderRadius: "8px", marginTop: "10px" }}>
                              <Form.Label style={{ fontWeight: "bold", color: "#6b7280", marginBottom: "12px" }}>ປ້ອນຈຳນວນແຕ່ລະໄຊຕ໌</Form.Label>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                                {shopSizes
                                  .filter((sz) => sp.sizeQuantities.hasOwnProperty(sz.id))
                                  .map((sz) => (
                                    <div key={sz.id} style={{ display: "flex", flexDirection: "column", width: "120px" }}>
                                      <Form.Label style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>Size {sz.value}</Form.Label>
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        placeholder="ປ້ອນຈຳນວນ.."
                                        value={sp.sizeQuantities[sz.id] === "" ? "" : sp.sizeQuantities[sz.id]}
                                        onChange={(e) =>
                                          handleProductSizeChange(
                                            index,
                                            sz.id,
                                            e.target.value
                                          )
                                        }
                                        style={{ fontSize: "14px" }}
                                      />
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Row>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>ຈຳນວນ</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={sp.quantity}
                            onChange={(e) =>
                              handleProductChange(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 1
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>ໝາຍເຫດລາຍການ</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="ປ້ອນລາຍລະອຽດເພີ່ມເຕີມ..."
                          value={sp.note}
                          onChange={(e) =>
                            handleProductChange(index, "note", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}

              <div
                style={{
                  background: "#eef2ff",
                  padding: "15px 20px",
                  borderRadius: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "20px",
                  border: "1px solid #c7d2fe",
                }}
              >
                <h5 style={{ margin: 0, fontWeight: "bold", color: "#1e40af" }}>
                  ລວມມູນຄ່າທັງໝົດ:
                </h5>
                <h4 style={{ margin: 0, fontWeight: "bold", color: "#1d4ed8" }}>
                  {calculateTotalOrderPrice().toLocaleString()} ກີບ
                </h4>
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={creatingOrder}>
          ຍົກເລີກ
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={creatingOrder}>
          {creatingOrder ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກອໍເດີ"}
        </Button>
      </Modal.Footer>
      <CreateCustomerModal
        show={showCreateCustomerModal}
        onHide={() => setShowCreateCustomerModal(false)}
        shopId={shopId}
        onCustomerCreated={(newCustomer) => {
          setSelectedCustomer(newCustomer);
          setCustomerSearchTerm(`${newCustomer.name || ""} ${newCustomer.lastname || ""}`.trim());
          setFormData({
            ...formData,
            firstName: newCustomer.name || "",
            lastName: newCustomer.lastname || "",
            phone: newCustomer.phone || "",
            province: newCustomer.address?.province || "",
            district: newCustomer.address?.district || "",
            village: newCustomer.address?.village || "",
          });
        }}
      />
    </Modal>
  );
};

export default CreateOrderModal;
